import { Box, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import React, { useState } from 'react';
import plusIcon from '../../../assets/add-white.svg';
import { ReactComponent as NextIcon } from '../../../assets/arrow-next.svg';
import { ReactComponent as PrevIcon } from '../../../assets/arrow-prev.svg';
import zeplinColor from '../../../theme/Color';
import CustomButton from '../../shared/CustomButton/CustomButton';
import CustomPopOver from '../../shared/CustomPopOver/CustomPopOver';
import style from './Calendar.module.css';
import CalendarMain from './CalendarMain/CalendarMain';
import CalendarPanel from './CalendarPanel/CalendarPanel';

const Calendar = () => {
  const [selectedDateRange, setSelectedDateRange] = useState({
    startDate: dayjs(),
    endDate: dayjs().add(6, 'day'),
  });
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [currentWeekDate, setCurrentWeekDate] = useState(dayjs());
  const [currentMonth, setCurrentMonth] = useState(dayjs().month());
  const [currentYear, setCurrentYear] = useState(dayjs().year());
  const [selectionMode, setSelectionMode] = useState<'month' | 'year'>('month');
  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const [collapsed, setCollapsed] = useState(false);

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const handleDateChange = (date: Dayjs | null) => {
    setSelectedDate(date);
    if (date) {
      const newMonth = date.month();
      const newYear = date.year();
      setCurrentWeekDate(date);

      if (newMonth !== currentMonth) {
        setCurrentMonth(newMonth);
      }

      if (newYear !== currentYear) {
        setCurrentYear(newYear);
      }
    }
  };
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const shortMonths = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const generateYears = () => {
    const years = [];
    for (let i = currentYear - 6; i <= currentYear + 5; i++) {
      years.push(i);
    }
    return years;
  };

  const years = generateYears();

  const handleMonthSelect = (monthIndex: number) => {
    setCurrentMonth(monthIndex);

    const newWeekDate = dayjs().year(currentYear).month(monthIndex).startOf('month');

    setCurrentWeekDate(newWeekDate);

    setSelectedDate(newWeekDate.startOf('isoWeek'));
    setSelectedDateRange({
      startDate: newWeekDate.startOf('isoWeek'),
      endDate: newWeekDate.endOf('isoWeek'),
    });
    setAnchorEl(null);
  };

  const handleYearSelect = (year: number) => {
    setCurrentYear(year);

    const newWeekDate = dayjs().year(year).month(currentMonth).startOf('month');

    setCurrentWeekDate(newWeekDate);

    setSelectedDate(newWeekDate.startOf('isoWeek'));
    setSelectedDateRange({
      startDate: newWeekDate.startOf('isoWeek'),
      endDate: newWeekDate.endOf('isoWeek'),
    });
    setAnchorEl(null);
  };

  const handlePrevious = () => {
    const previousWeek = currentWeekDate.subtract(1, 'week');
    const previousWeekMonth = previousWeek.month();
    const previousWeekYear = previousWeek.year();
    setCurrentWeekDate(previousWeek);

    setSelectedDate(previousWeek.startOf('isoWeek'));

    if (previousWeekMonth !== currentMonth) {
      setCurrentMonth(previousWeekMonth);
    }

    if (previousWeekYear !== currentYear) {
      setCurrentYear(previousWeekYear);
    }
  };

  const handleNext = () => {
    const nextWeek = currentWeekDate.add(1, 'week');
    const nextWeekMonth = nextWeek.month();
    const nextWeekYear = nextWeek.year();
    setCurrentWeekDate(nextWeek);

    setSelectedDate(nextWeek.startOf('isoWeek'));
    if (nextWeekMonth !== currentMonth) {
      setCurrentMonth(nextWeekMonth);
    }

    if (nextWeekYear !== currentYear) {
      setCurrentYear(nextWeekYear);
    }
  };
  const handleMonthYearChange = (month: number, year: number) => {
    setCurrentMonth(month);
    setCurrentYear(year);
  };

  return (
    <Box>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '24px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Typography
            variant='responsiveHeading3'
            onClick={(e: any) => {
              setAnchorEl(e.currentTarget);
              setSelectionMode('month');
            }}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                color: zeplinColor.Brand1,
              },
            }}
          >
            {months[currentMonth]}{' '}
          </Typography>

          <Typography
            variant='responsiveHeading3'
            onClick={(e: any) => {
              setAnchorEl(e.currentTarget);
              setSelectionMode('year');
            }}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                color: zeplinColor.Brand1,
              },
            }}
          >
            {currentYear}
          </Typography>

          <Box sx={{ display: 'flex', gap: '4px' }}>
            <Box
              sx={{
                padding: '5px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',

                borderRadius: '50%',
                '&:hover': { backgroundColor: zeplinColor.Neutral4 },
              }}
            >
              <PrevIcon
                className={style['next-prev-icon']}
                onClick={handlePrevious}
                style={{ cursor: 'pointer' }}
              />
            </Box>
            <Box
              sx={{
                padding: '5px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',

                borderRadius: '50%',
                '&:hover': { backgroundColor: zeplinColor.Neutral4 },
              }}
            >
              <NextIcon
                className={style['next-prev-icon']}
                onClick={handleNext}
                style={{ cursor: 'pointer' }}
              />
            </Box>
            {/* <Box sx={{ width: '100%' }}>
              <CustomButton
                disabled={false}
                onClick={() => {}}
                variant='contained'
                color={'primary'}
                buttonVariant='responsiveButtonLarge'
                style={{
                  width: '100%',
                  borderRadius: { xs: '10px', xl: '14px' },
                  height: { xs: 30, xl: 48 },
                  padding: { xs: '10px 12px', xl: '14px 16px' },
                  '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
                }}
                textStyle={{ color: zeplinColor.Neutral1 }}
                text={'Create Event'}
                icon={<img src={plusIcon} alt='add sign' width={'20px'} height={'20px'} />}
              />
            </Box> */}
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: { xs: '12px', xl: '24px' } }}>
        <Box
          sx={{
            flex: collapsed ? 0 : 1,

            transition: 'flex 0.3s ease-in-out',
          }}
        >
          <CalendarPanel
            onDateChange={handleDateChange}
            currentMonth={currentMonth}
            currentYear={currentYear}
            onMonthYearChange={handleMonthYearChange}
            currentWeekDate={currentWeekDate}
            collapsed={collapsed}
            setCollapsed={setCollapsed}
          />
        </Box>
        <Box
          sx={{
            flex: 3,
            overflowX: 'auto',
          }}
        >
          <CalendarMain selectedDateRange={selectedDateRange} selectedDate={selectedDate} />
        </Box>
      </Box>

      <CustomPopOver
        setAnchorEl={setAnchorEl}
        anchorEl={anchorEl}
        id={id}
        sx={{
          borderRadius: '14px',
          boxShadow: 'rgba(0, 0, 0, 0.15)',
          marginTop: '8px',
          marginRight: '2rem',
          marginLeft: '-4rem',
        }}
      >
        <Box sx={{ padding: '16px', minWidth: '280px' }}>
          {selectionMode === 'month' && (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '12px',
                }}
              >
                {shortMonths.map((month, index) => (
                  <Box
                    key={month}
                    onClick={() => handleMonthSelect(index)}
                    sx={{
                      padding: '12px 8px',
                      textAlign: 'center',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor:
                        index === currentMonth ? zeplinColor.BasicBlack : zeplinColor.Neutral1,
                      color: index === currentMonth ? zeplinColor.Neutral1 : zeplinColor.BasicBlack,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor:
                          index === currentMonth ? zeplinColor.BasicBlack : zeplinColor.Neutral3,
                      },
                    }}
                  >
                    <Typography
                      variant='body2'
                      sx={{
                        fontWeight: index === currentMonth ? 600 : 400,
                        fontSize: '14px',
                      }}
                    >
                      {month}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}

          {selectionMode === 'year' && (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '12px',
                  maxHeight: '240px',
                  overflowY: 'auto',
                }}
              >
                {years.map((year: number, index: number) => (
                  <Box
                    key={year}
                    onClick={() => handleYearSelect(year)}
                    sx={{
                      padding: '12px 8px',
                      textAlign: 'center',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor:
                        year === currentYear ? zeplinColor.BasicBlack : zeplinColor.Neutral1,
                      color: year === currentYear ? zeplinColor.Neutral1 : zeplinColor.BasicBlack,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor:
                          year === currentYear ? zeplinColor.BasicBlack : zeplinColor.Neutral3,
                        transform: 'scale(1.02)',
                      },
                    }}
                  >
                    <Typography
                      variant='body2'
                      sx={{
                        fontWeight: year === currentYear ? 600 : 400,
                        fontSize: '14px',
                      }}
                    >
                      {year}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>
      </CustomPopOver>
    </Box>
  );
};

export default Calendar;
